import React, { useState, useEffect } from 'react';
import { adminApi } from './AdminLayout';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { toast } from 'sonner';

const FeeSchedulePanel = ({ token, feeSchedule, onUpdate }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(feeSchedule?.campaignId?.toString() || '');
  const [items, setItems] = useState(feeSchedule?.items || []);
  const [customNotes, setCustomNotes] = useState(feeSchedule?.customNotes || '');
  const [saving, setSaving] = useState(false);
  const [applyingProposal, setApplyingProposal] = useState(false);

  const specialistProposal = feeSchedule?.specialistProposal;

  useEffect(() => {
    adminApi.get('/admin/fee-templates')
      .then((res) => setTemplates(res.data.campaigns || res.data || []))
      .catch(() => toast.error('Failed to load fee templates'));
  }, []);

  const handleTemplateChange = async (campaignId) => {
    setSelectedCampaign(campaignId);
    if (!campaignId) return;

    try {
      const response = await adminApi.get(`/admin/fee-templates/${campaignId}`);
      const campaign = response.data;
      const newItems = (campaign.fees || campaign.items || []).map((fee) => ({
        feeType: fee.feeType || fee.type || '',
        name: fee.name || fee.description || '',
        rate: fee.rate != null ? fee.rate : null,
        fixedAmount: fee.fixedAmount != null ? fee.fixedAmount : null,
        description: fee.description || '',
      }));
      setItems(newItems);
      toast.success(`Loaded template: ${campaign.name || `Campaign ${campaignId}`}`);
    } catch {
      toast.error('Failed to load template details');
    }
  };

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => {
    setItems((prev) => [...prev, { feeType: '', name: '', rate: null, fixedAmount: null, description: '' }]);
  };

  const removeItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleApplyProposal = async () => {
    setApplyingProposal(true);
    try {
      const response = await adminApi.post(`/admin/applications/${token}/fee-proposal`, {
        proposal: specialistProposal,
      });
      const updated = response.data;
      if (updated.feeSchedule?.items) {
        setItems(updated.feeSchedule.items);
      }
      toast.success('Specialist proposal applied');
      if (onUpdate) onUpdate(updated);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply proposal');
    } finally {
      setApplyingProposal(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        feeSchedule: {
          campaignId: selectedCampaign ? parseInt(selectedCampaign) : null,
          items,
          customNotes: customNotes || null,
        },
      };
      const response = await adminApi.patch(`/admin/applications/${token}/review`, payload);
      toast.success('Fee schedule saved');
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save fee schedule');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
      <h3 className="text-lg font-semibold text-slate-100 mb-4">Fee Schedule</h3>

      <div className="space-y-4">
        <div>
          <Label className="text-slate-200">Campaign Template</Label>
          <Select value={selectedCampaign} onValueChange={handleTemplateChange}>
            <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-100" data-testid="campaign-select">
              <SelectValue placeholder="Select a campaign template" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {templates.map((t) => (
                <SelectItem
                  key={t.id || t.campaignId}
                  value={(t.id || t.campaignId).toString()}
                  className="text-slate-200 focus:bg-slate-700 focus:text-slate-100"
                >
                  {t.name || `Campaign ${t.id || t.campaignId}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {specialistProposal && (
          <div className="bg-slate-900 rounded-md p-4 border border-blue-500/30">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-blue-300">Specialist Proposal Available</h4>
              <Button
                onClick={handleApplyProposal}
                disabled={applyingProposal}
                size="sm"
                className="bg-blue-600 text-white hover:bg-blue-500"
                data-testid="apply-proposal-btn"
              >
                {applyingProposal ? 'Applying...' : 'Apply Proposal'}
              </Button>
            </div>
            <p className="text-xs text-slate-400">
              Proposed at: {feeSchedule?.specialistProposedAt
                ? new Date(feeSchedule.specialistProposedAt).toLocaleString()
                : 'Unknown'}
            </p>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-slate-200">Line Items</Label>
            <Button
              onClick={addItem}
              variant="outline"
              size="sm"
              className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700"
            >
              + Add Item
            </Button>
          </div>
          <div className="rounded-md border border-slate-700 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400 text-xs">Fee Type</TableHead>
                  <TableHead className="text-slate-400 text-xs">Name</TableHead>
                  <TableHead className="text-slate-400 text-xs">Rate (%)</TableHead>
                  <TableHead className="text-slate-400 text-xs">Fixed ($)</TableHead>
                  <TableHead className="text-slate-400 text-xs">Description</TableHead>
                  <TableHead className="text-slate-400 text-xs w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 text-sm py-6">
                      No fee items. Select a template or add items manually.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, i) => (
                    <TableRow key={i} className="border-slate-700">
                      <TableCell>
                        <Input
                          value={item.feeType || ''}
                          onChange={(e) => updateItem(i, 'feeType', e.target.value)}
                          className="bg-slate-900 border-slate-600 text-slate-100 h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.name || ''}
                          onChange={(e) => updateItem(i, 'name', e.target.value)}
                          className="bg-slate-900 border-slate-600 text-slate-100 h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.rate ?? ''}
                          onChange={(e) => updateItem(i, 'rate', e.target.value ? parseFloat(e.target.value) : null)}
                          className="bg-slate-900 border-slate-600 text-slate-100 h-8 text-xs w-20"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.fixedAmount ?? ''}
                          onChange={(e) => updateItem(i, 'fixedAmount', e.target.value ? parseFloat(e.target.value) : null)}
                          className="bg-slate-900 border-slate-600 text-slate-100 h-8 text-xs w-20"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={item.description || ''}
                          onChange={(e) => updateItem(i, 'description', e.target.value)}
                          className="bg-slate-900 border-slate-600 text-slate-100 h-8 text-xs"
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => removeItem(i)}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >
                          X
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div>
          <Label className="text-slate-200">Custom Notes</Label>
          <textarea
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-md bg-slate-900 border border-slate-700 text-slate-100 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-slate-200 text-slate-900 hover:bg-slate-100 font-medium"
          data-testid="save-fees-btn"
        >
          {saving ? 'Saving...' : 'Save Fee Schedule'}
        </Button>
      </div>
    </div>
  );
};

export default FeeSchedulePanel;
